/**
 * The Business Opportunities page's List view: every idea that matches the
 * filters, scored on the weighted dimensions (Configuration →
 * Business Opportunities sets them). Sorting and paging run on the server; rows still being researched
 * refresh on their own. Authors (and admins) rerun research; admins park,
 * archive and reactivate.
 */

import { useEffect, useState } from 'react';
import { Grid3x3, Lightbulb } from 'lucide-react';

import type { Dimension, IdeaList, IdeaListParams } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { Pagination } from '@/ui/Pagination';
import { SortHeader } from '@/ui/SortHeader';
import { sortPatch } from '@/ui/useSort';

import { IdeaTableDialogs } from './IdeaTableDialogs';
import { IdeaTableRow, type PendingIdeaAction } from './IdeaTableRow';

// Columns read top-down in their natural order (A–Z, Active first, Go first)
// on the first click; scores read highest first.
const ASCENDING_FIRST = new Set(['title', 'industry', 'disposition', 'verdict']);

function IdeaTableHead({
  params,
  dimensions,
  onSort,
}: {
  params: IdeaListParams;
  dimensions: Dimension[];
  onSort: (key: string) => void;
}) {
  const sort = { sort: params.sort, order: params.order, onSort };
  return (
    <thead>
      <tr>
        <SortHeader label="Industry" sortKey="industry" {...sort} className="col-industry" />
        <SortHeader label="Idea" sortKey="title" {...sort} className="col-idea" />
        <SortHeader label="Status" sortKey="disposition" {...sort} />
        <SortHeader label="Verdict" sortKey="verdict" {...sort} />
        <SortHeader label="Total" sortKey="total" {...sort} className="num" />
        {dimensions.map((d) => (
          <SortHeader key={d.key} label={d.label} sortKey={d.key} {...sort} className="num dim-col" />
        ))}
        <th scope="col"><span className="sr-only">Actions</span></th>
      </tr>
    </thead>
  );
}

function NoIdeasYet({ onSubmitIdea }: { onSubmitIdea: () => void }) {
  const { can } = useSession();
  return (
    <>
      <h3>No ideas yet</h3>
      {can('idea.submit') ? (
        <>
          <p>Submit the first one; research scores it and it appears here.</p>
          <p className="empty-actions">
            <button type="button" className="btn btn-primary" onClick={onSubmitIdea}>
              <Lightbulb aria-hidden="true" /> Submit an idea
            </button>
          </p>
        </>
      ) : (
        <p>Ideas appear here once they are submitted and scored.</p>
      )}
    </>
  );
}

function EmptyRow({ columns, filtered, onSubmitIdea }: { columns: number; filtered: boolean; onSubmitIdea: () => void }) {
  return (
    <tr>
      <td colSpan={columns}>
        <div className="empty">
          <div className="e-icon"><Grid3x3 aria-hidden="true" /></div>
          {filtered ? (
            <>
              <h3>No ideas match</h3>
              <p>Clear the search or the filters to see the rest.</p>
            </>
          ) : (
            <NoIdeasYet onSubmitIdea={onSubmitIdea} />
          )}
        </div>
      </td>
    </tr>
  );
}

export function IdeaTable({
  data,
  params,
  dimensions,
  filtered,
  showDimensions,
  refine,
  setPage,
  onSubmitIdea,
}: {
  data: IdeaList;
  params: IdeaListParams;
  dimensions: Dimension[];
  filtered: boolean;
  showDimensions: boolean;
  refine: (patch: Partial<IdeaListParams>) => void;
  setPage: (page: number) => void;
  onSubmitIdea: () => void;
}) {
  const [pending, setPending] = useState<PendingIdeaAction | null>(null);
  const rows = data.ideas;

  function onSort(key: string) {
    refine(sortPatch({ sort: params.sort, order: params.order }, key, ASCENDING_FIRST));
  }

  // A page can empty under the reader (an idea parked or archived away from
  // the filter): fall back to the last page that still has rows.
  const lastPage = Math.max(1, Math.ceil(data.total / params.page_size));
  useEffect(() => {
    if (params.page > lastPage) setPage(lastPage);
  }, [lastPage, params.page, setPage]);

  return (
    <>
      <div className={`table-scroll idea-matrix${showDimensions ? ' show-dims' : ''}`}>
        <table className="data zebra compact">
          <IdeaTableHead params={params} dimensions={dimensions} onSort={onSort} />
          <tbody>
            {rows.length === 0 ? (
              <EmptyRow columns={6 + dimensions.length} filtered={filtered} onSubmitIdea={onSubmitIdea} />
            ) : (
              rows.map((idea) => <IdeaTableRow key={idea.id} idea={idea} dimensions={dimensions} setPending={setPending} />)
            )}
          </tbody>
        </table>
      </div>
      {data.total > 0 && (
        <Pagination
          page={params.page}
          pageSize={params.page_size}
          total={data.total}
          noun="ideas"
          onPage={setPage}
          onPageSize={(page_size) => refine({ page_size })}
        />
      )}

      <IdeaTableDialogs pending={pending} setPending={setPending} />
    </>
  );
}
